import React, { Suspense, lazy, useState } from 'react';
import { styled } from '@mui/material/styles';
import { ButtonBase, Box, Container, Link, Stack } from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import StraightenIcon from '@mui/icons-material/Straighten';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Typography from '../../components/Typography';
import InitialData from '../../../InitialData';
import { DEFAULT_LOCALE, normalizeLocale } from '../../../locales/languages';

const ImageBackdrop = styled('div')(({ theme }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  background:
    'linear-gradient(180deg, rgba(0, 0, 0, 0.25) 0%, rgba(0, 0, 0, 0.42) 48%, rgba(0, 0, 0, 0.78) 100%)',
  opacity: 0.54,
  transition: theme.transitions.create('opacity'),
}));

const ImageIconButton = styled(ButtonBase)(({ theme }) => ({
  position: 'relative',
  display: 'block',
  padding: 0,
  borderRadius: 0,
  height: '40vh',
  [theme.breakpoints.down('md')]: {
    width: '100% !important',
    height: 150,
    '& .imageTitle': {
      padding: `${theme.spacing(1)} ${theme.spacing(2)} 10px`,
      transform: 'translateY(-22px)',
    },
    '& .imageMeta': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  [theme.breakpoints.down('sm')]: {
    height: 132,
  },
  '&:hover': {
    zIndex: 1,
  },
  '&:hover .imageBackdrop, &:focus-visible .imageBackdrop': {
    opacity: 0.84,
  },
  '&:hover .imageMarked': {
    opacity: 0,
  },
  '&:hover .imageMeta, &:focus-visible .imageMeta': {
    opacity: 1,
    transform: 'translateY(0)',
  },
  '&:hover .imageTitle': {
    border: '4px solid currentColor',
  },
  '& .imageTitle': {
    position: 'relative',
    padding: `${theme.spacing(2)} ${theme.spacing(4)} 14px`,
  },
  '& .imageMarked': {
    height: 3,
    width: 18,
    background: theme.palette.common.white,
    position: 'absolute',
    bottom: -2,
    left: 'calc(50% - 9px)',
    transition: theme.transitions.create('opacity'),
  },
  '& .imageMeta': {
    opacity: 0,
    transform: 'translateY(12px)',
    transition: theme.transitions.create(['opacity', 'transform']),
  },
}));

const widths = ['40%', '20%', '40%', '38%', '38%', '24%', '40%', '20%', '40%'];

const mapApartmentsToImages = (apartments) => {
  return apartments.map((apartment, index) => ({
    url: apartment.photos[0],
    title: apartment.name,
    width: widths[index],
    id: apartment.id,
    size: apartment.size,
    capacity: apartment.capacity || 2,
  }));
};

const ApartmentModal = lazy(() => import('./ApartmentModal'));

const slugify = (value) => {
  if (!value) {
    return '';
  }
  return String(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-');
};

export default function Apartments() {
  const [selectedApartmentId, setSelectedApartmentId] = useState(null);
  const navigate = useNavigate();
  const images = mapApartmentsToImages(InitialData);
  const { t, i18n } = useTranslation();
  const activeLocale = normalizeLocale(i18n.language) || DEFAULT_LOCALE;
  const openApartmentDetail = (apartmentId) => {
    const apartment = InitialData.find((apt) => apt.id === apartmentId);
    if (apartment) {
      const slug = slugify(
        apartment.slug || apartment.name || apartment.longName || apartment.id,
      );
      navigate(`/${activeLocale}/rooms/${slug}/`);
    }
  };

  const openApartmentModal = (apartmentId) => {
    setSelectedApartmentId(apartmentId);
  };

  const closeApartmentModal = () => {
    setSelectedApartmentId(null);
  };
  return (
    <Container
      component="section"
      name="apartments"
      sx={{ my: { xs: 4, sm: 5 }, px: { xs: 2, sm: 3 } }}
    >
      <Typography variant="h4" marked="center" align="center" component="h2">
        {t('productCategories.text')}
      </Typography>
      <Box
        sx={{
          mt: { xs: 3, sm: 5 },
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 1, md: 0 },
        }}
      >
        {images.map((image) => (
          <ImageIconButton
            key={image.title}
            style={{
              width: image.width,
            }}
            onClick={() => openApartmentDetail(image.id)}
          >
            <Box
              component="img"
              src={image.url}
              alt={`${image.title} preview`}
              loading="lazy"
              decoding="async"
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center 40%',
              }}
            />
            <ImageBackdrop className="imageBackdrop" />
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'common.white',
              }}
            >
              <Typography
                component="h3"
                variant="h6"
                color="inherit"
                className="imageTitle"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  textAlign: 'center',
                }}
              >
                {image.title}
                <div className="imageMarked" />
              </Typography>
              <Box
                className="imageMeta"
                sx={{
                  position: 'absolute',
                  left: { xs: 10, sm: 16 },
                  right: { xs: 10, sm: 16 },
                  bottom: { xs: 10, sm: 16 },
                  display: 'flex',
                  justifyContent: { xs: 'center', sm: 'space-between' },
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  pointerEvents: 'none',
                  px: { xs: 1.25, sm: 1.5 },
                  py: { xs: 1, sm: 1.25 },
                  bgcolor: 'rgba(10, 12, 16, 0.76)',
                  border: '1px solid rgba(255, 255, 255, 0.28)',
                  borderRadius: 1,
                  boxShadow: '0 12px 30px rgba(0, 0, 0, 0.34)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 0,
                    gap: 0.75,
                    color: 'common.white',
                  }}
                >
                  <StraightenIcon sx={{ fontSize: { xs: 17, sm: 19 } }} />
                  <Typography
                    component="span"
                    sx={{
                      fontSize: { xs: 12, sm: 14 },
                      fontWeight: 700,
                      lineHeight: 1.15,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {image.size} m²
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 0,
                    gap: 0.75,
                    color: 'common.white',
                  }}
                >
                  <PeopleAltIcon sx={{ fontSize: { xs: 17, sm: 19 } }} />
                  <Typography
                    component="span"
                    sx={{
                      fontSize: { xs: 12, sm: 14 },
                      fontWeight: 700,
                      lineHeight: 1.15,
                      minWidth: 0,
                      textAlign: 'right',
                    }}
                  >
                    {t('rooms.capacityLabel', { count: image.capacity })}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </ImageIconButton>
        ))}
      </Box>
      <Stack spacing={1} sx={{ mt: 4 }} component="nav" aria-label="Rooms">
        <Typography variant="h6" align="center">
          {t('rooms.linksTitle')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          {InitialData.map((apartment) => {
            const slug = slugify(
              apartment.slug ||
                apartment.name ||
                apartment.longName ||
                apartment.id,
            );
            const to = `/${activeLocale}/rooms/${slug}/`;
            return (
              <Link
                key={apartment.id}
                component={RouterLink}
                to={to}
                underline="hover"
                sx={{ fontWeight: 600 }}
              >
                {apartment.longName || apartment.name}
              </Link>
            );
          })}
        </Box>
      </Stack>
      {selectedApartmentId && (
        <Suspense fallback={null}>
          <ApartmentModal
            apartmentId={selectedApartmentId}
            onClose={closeApartmentModal}
          />
        </Suspense>
      )}
    </Container>
  );
}
